import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { appsService, consentsService, log, notificationService, t } from "../../../services";
import { appManageTypes, consentStatuses, strCode } from "../../../consts";
import { responseUtil } from "../../../utils";

import { dispatch } from "../../../state/store";
import { setIsMarketsReassigned, setSelectedApp } from "../../../state/ducks";

import {
  CircleIconButton,
  DiscardModal,
  FlexContainers,
  Paragraph2,
  SVG,
  WrapperContainer
} from "../../../components";
import { MarketList, MarketTableWrapper } from "./styled";
import { MarketItem } from "./components/MarketItem/MarketItem";

const MarketsTabTable = ({
  markets,
  marketsWithoutConsents,
  openModal,
  onConsentsOpen,
  onLocationsOpen,
  onMarketDelete,
  isReadOnlyAccess,
  onFeaturesSettingsOpen,
  onLocalizationsOpen
}) => {
  const selectedApp = useSelector((store) => store.apps.selectedApp);
  const loading = useSelector((store) => store.markets?.loading);
  const error = useSelector((store) => store.markets?.error);
  const manageType = useSelector((store) => store.apps.manageType);
  const defaultFeatures = useSelector((store) => store.apps?.selectedApp.defaultSettings);
  const isMarketsReassigned = useSelector((store) => store.apps?.isMarketsReassigned);
  const prevAppType = useSelector((store) => store.apps?.prevAppType);

  const [isMarketsReassigning, setIsMarketsReassigning] = useState(false);
  const [deleteMarketModalActive, setDeleteMarketModalActive] = useState(false);
  const [marketToDelete, setMarketToDelete] = useState(null);

  const appMarkets = useMemo(() => {
    if (selectedApp.marketConfigs) {
      const selectedAppMarkets = selectedApp.marketConfigs.map((market) => market.market);

      return markets.filter((market) => selectedAppMarkets.includes(market.market));
    }

    return [];
  }, [selectedApp, markets]);

  const fetchMarkets = async () => {
    if (
      manageType === appManageTypes.FROM_EXISTING &&
      !isMarketsReassigned &&
      !isMarketsReassigning
    ) {
      setIsMarketsReassigning(true);

      try {
        const preparedMarkets = appMarkets.map((market) => {
          return {
            appType: selectedApp.appType,
            market: market.market,
            settings: defaultFeatures
          };
        });

        const resArr = await Promise.all(
          preparedMarkets.map((market) => {
            return appsService.createAppTypeMarketConfig(market);
          })
        );

        if (resArr.every((res) => responseUtil.isResponseSuccessful(res))) {
          const consents = await Promise.all(
            preparedMarkets.map((market) => {
              return consentsService.fetchConsents(market.market, prevAppType);
            })
          );

          await Promise.all(
            consents.map(async (marketConsents) => {
              const activeConsent = marketConsents.find(
                (consent) => consent.status === consentStatuses.active
              );

              if (activeConsent) {
                const { id } = await consentsService.publishNewConsent({
                  appType: selectedApp.appType,
                  id: null,
                  market: activeConsent.market
                });

                const consentDetails = await consentsService.fetchConsent(id);
                const activeConsentDetails = await consentsService.fetchConsent(activeConsent.id);

                await consentsService.updateConsentElements({
                  id: consentDetails.id,
                  items: activeConsentDetails.items
                });

                await consentsService.activateConsent({
                  appType: selectedApp.appType,
                  id: consentDetails.id,
                  market: activeConsent.market
                });
              }
            })
          );

          dispatch(
            setSelectedApp({
              ...selectedApp,
              marketConfigs: [...preparedMarkets]
            })
          );
        }
      } catch (err) {
        log.error("MarketsTabTable: Failed to create a market:", err);
        notificationService.showError({
          title: t(strCode.APP_MARKETS_CREATE_ERROR_TITLE),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });
      }

      setIsMarketsReassigning(false);

      dispatch(setIsMarketsReassigned(true));
    }
  };

  useEffect(() => {
    fetchMarkets();
  }, [appMarkets, selectedApp]);

  return (
    <MarketTableWrapper width="100%">
      {error ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={50}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.MARKETS_FETCH_ERROR)}</Paragraph2>
        </WrapperContainer>
      ) : loading || isMarketsReassigning ? (
        <Paragraph2>{t(strCode.LOADING)}</Paragraph2>
      ) : (
        <MarketList Container={FlexContainers.FlexRowStart}>
          {appMarkets &&
            appMarkets?.map((market) => {
              return (
                <MarketItem
                  key={market?.market}
                  market={market?.market}
                  name={market?.name}
                  marketsWithoutConsents={marketsWithoutConsents}
                  isReadOnlyAccess={isReadOnlyAccess}
                  onConsentsOpen={onConsentsOpen}
                  onSettingsOpen={onFeaturesSettingsOpen}
                  onLocationsOpen={onLocationsOpen}
                  onLocalizationsOpen={onLocalizationsOpen}
                  onRemove={() => {
                    setDeleteMarketModalActive(true);
                    setMarketToDelete(market?.market);
                  }}
                />
              );
            })}

          <WrapperContainer Container={FlexContainers.FlexRowStart} height={56}>
            <CircleIconButton onClick={openModal} disabled={isMarketsReassigning}>
              <SVG.PlusIcon />
            </CircleIconButton>
          </WrapperContainer>

          <DiscardModal
            title={strCode.REMOVE_APP_MARKET_CONFIRMATION_TITLE}
            text={t(strCode.REMOVE_APP_MARKET_CONFIRMATION_TEXT, { market: marketToDelete })}
            confirmBtnText={strCode.REMOVE_BTN}
            loading={isMarketsReassigning}
            active={deleteMarketModalActive}
            setActive={setDeleteMarketModalActive}
            onDiscard={() => {
              onMarketDelete(marketToDelete);
              setDeleteMarketModalActive(false);
              setMarketToDelete(null);
            }}
          />
        </MarketList>
      )}
    </MarketTableWrapper>
  );
};

MarketsTabTable.propTypes = {
  markets: PropTypes.arrayOf(
    PropTypes.shape({
      activeAppTypes: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      activeUserCount: PropTypes.number.isRequired,
      country: PropTypes.string.isRequired,
      market: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  marketsWithoutConsents: PropTypes.arrayOf(PropTypes.string).isRequired,
  openModal: PropTypes.func.isRequired,
  onConsentsOpen: PropTypes.func.isRequired,
  onLocationsOpen: PropTypes.func.isRequired,
  onMarketDelete: PropTypes.func.isRequired,
  isReadOnlyAccess: PropTypes.bool.isRequired,
  onFeaturesSettingsOpen: PropTypes.func.isRequired,
  onLocalizationsOpen: PropTypes.func.isRequired
};

export default MarketsTabTable;
